import React from 'react';

import { Checkbox } from '@column/ui';

import { type CardDisputeReason } from '~/lib/disputes/labels';

import {
  type DisputeReasonFormState,
  type DisputeRequirementContext,
  REASON_WITHOUT_DETAILS,
  type ReasonFieldKey,
  type ReasonQuestion,
  questionAnswer,
  reasonQuestions,
} from './questionnaire';
import { ReasonBooleanField, ReasonDateField, ReasonSelectField, ReasonTextField } from './ReasonFields';

export interface DisputeReasonFormProps {
  reason: CardDisputeReason;
  value: DisputeReasonFormState;
  onChange: (value: DisputeReasonFormState) => void;
  /** Dispute currency; drives the incorrect-amount receipt parsing. */
  currency: string;
  /** The clock and disputed transaction that conditional questions read. Omitted, transaction-dependent questions are optional. */
  context?: DisputeRequirementContext;
  disabled?: boolean;
  /** Missing required-field keys reported by `missingReasonFields`; shown inline per field. */
  missingFields?: ReasonFieldKey[];
}

type AnswerValue = string | boolean | string[] | undefined;

/**
 * Renders the questions `reasonQuestions` asks for the current answers, so the form shows exactly
 * the branches readiness checks and serialization sends. Hard-stopped answers explain themselves
 * inline as soon as they are given; missing answers are flagged once the caller asks. Reasons
 * without caller-supplied details render nothing here; their narrative is collected separately in
 * the evidence step.
 */
export const DisputeReasonForm: React.FC<DisputeReasonFormProps> = ({
  reason,
  value,
  onChange,
  currency,
  context,
  disabled,
  missingFields = [],
}) => {
  if (REASON_WITHOUT_DETAILS.includes(reason)) {
    return null;
  }
  const questions = reasonQuestions(reason, value, currency, context);
  if (questions.length === 0) {
    return null;
  }

  const setAnswer = (field: ReasonFieldKey, answer: AnswerValue) => {
    const current = ((value as Partial<Record<string, Record<string, AnswerValue>>>)[reason] ?? {}) as Record<
      string,
      AnswerValue
    >;
    onChange({ ...value, [reason]: { ...current, [field]: answer } });
  };

  return (
    <div className="flex flex-col gap-4" data-testid="dispute-reason-form">
      {questions.map((question) => (
        <ReasonQuestionField
          key={question.field}
          question={question}
          value={questionAnswer(question, value, reason)}
          onChange={(answer) => setAnswer(question.field, answer)}
          disabled={disabled}
          missing={missingFields.includes(question.field)}
        />
      ))}
    </div>
  );
};

interface ReasonQuestionFieldProps {
  question: ReasonQuestion;
  value: AnswerValue;
  onChange: (value: AnswerValue) => void;
  disabled?: boolean;
  missing: boolean;
}

const ReasonQuestionField: React.FC<ReasonQuestionFieldProps> = ({ question, value, onChange, disabled, missing }) => {
  const testId = question.testId ?? `reason-${question.field.replace(/_/g, '-')}`;
  const error = question.hardStop ?? (missing ? 'This answer is required.' : undefined);
  const stringValue = typeof value === 'string' ? value : undefined;

  switch (question.kind) {
    case 'select':
      return (
        <ReasonSelectField
          label={question.label}
          description={question.description}
          value={stringValue}
          onChange={onChange}
          options={question.options ?? []}
          required={question.required}
          disabled={disabled}
          error={error}
          testId={testId}
        />
      );
    case 'text':
    case 'multiline':
    case 'amount':
      return (
        <ReasonTextField
          label={question.label}
          description={question.description}
          value={stringValue}
          onChange={onChange}
          placeholder={question.placeholder}
          required={question.required}
          disabled={disabled}
          maxLength={question.maxLength}
          multiline={question.kind === 'multiline'}
          error={error}
          testId={testId}
        />
      );
    case 'date':
      return (
        <ReasonDateField
          label={question.label}
          description={question.description}
          value={stringValue}
          onChange={onChange}
          required={question.required}
          disabled={disabled}
          error={error}
          testId={testId}
        />
      );
    case 'boolean':
      return (
        <ReasonBooleanField
          label={question.label}
          description={question.description}
          value={typeof value === 'boolean' ? value : undefined}
          onChange={onChange}
          required={question.required}
          disabled={disabled}
          error={error}
          testId={testId}
        />
      );
    case 'contact_methods': {
      const methods = Array.isArray(value) ? value : [];
      const toggleMethod = (method: string, checked: boolean) =>
        onChange(checked ? [...methods, method] : methods.filter((entry) => entry !== method));
      return (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-foreground">
            {question.label}
            {question.required && <span className="text-destructive"> *</span>}
          </p>
          {(question.hardStop || missing) && (
            <p className="text-xs text-destructive" data-testid="reason-field-error">
              {question.hardStop ?? 'Select at least one contact method.'}
            </p>
          )}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(question.options ?? []).map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2 text-sm text-foreground"
                data-testid={`reason-contact-method-${option.value}`}
              >
                <Checkbox
                  checked={methods.includes(option.value)}
                  disabled={disabled}
                  onCheckedChange={(checked) => toggleMethod(option.value, checked === true)}
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>
      );
    }
    default:
      return null;
  }
};
